import React, { useState } from "react";
import { z } from "zod";

import { Button } from "@picklebet/ui";
import { signOut } from "../services/session";
import { useShellStore } from "@/features/shell/stores/shellStore";
import {
  useCustomerLicence,
  useProfile,
} from "@/hooks/firestore/user/useProfile";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@picklebet/ui";
import FullScreenModal from "@/features/shell/components/FullScreenModal/FullScreenModal";
import { PhoneInput } from "@picklebet/ui";
import { Typography } from "@picklebet/ui";
import { useForm } from "react-hook-form";
import type { Country } from "react-phone-number-input/input-mobile";
import { isValidPhoneNumber } from "utilities/helpers";
import { sendMessage } from "utilities/sendMessage";
import { validateProfile } from "../services/session";
import { CustomerLicenceOptions } from "../stores/authStore";

import * as styles from "./ProfileCompletionModal.module.scss";

const createSchema = (country: string) => {
  return z.object({
    phone: z
      .any()
      .transform((val) => (val ?? "") as string)
      .refine(
        (value) => {
          if (country === "AU") {
            return value.startsWith("+614") && isValidPhoneNumber(value);
          }
          return isValidPhoneNumber(value);
        },
        {
          message: "Please enter a valid mobile number",
        },
      ),
  });
};

type FormValues = z.infer<ReturnType<typeof createSchema>>;

export const PROFILE_COMPLETION_MODAL = "profileCompletionModal";

export const ProfileCompletionModal = () => {
  const { profile } = useProfile();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(createSchema(profile?.country || "AU")),
  });

  const [isPosting, setIsPosting] = useState(false);

  const licence = useCustomerLicence();
  const isNTRC = licence === CustomerLicenceOptions.NTRWC;
  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  const modal = useShellStore((state) => state.modal);
  const setModal = useShellStore((state) => state.setModal);
  const isOpen = modal === "profileCompletionModal";

  const handleClose = async () => {
    if (!profile?.phone && isNTRC) {
      await signOut();
    }

    setModal(null);
  };

  const onSubmit = handleSubmit(async ({ phone }) => {
    if (!profile) return;

    const [validation] = await validateProfile(profile.username, "", {
      phone,
    });

    if (validation.length > 0) {
      const invalidPhone = validation.find((e) => e.propertyPath === "phone");
      if (invalidPhone) {
        setError("phone", {
          type: "manual",
          message: invalidPhone.message,
        });

        return;
      }
    }

    setIsPosting(true);

    const [_, error] = await sendMessage("complete-user-profile", { phone });

    if (error) {
      setError("phone", {
        message: "Something went wrong. Try again later",
      });
      setIsPosting(false);

      return;
    }

    setModal(null);
    setIsPosting(false);
  });

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Incomplete Profile`}
    >
      <div className={styles.content}>
        {isNTRC ? (
          <Typography variant={`body`}>
            Due to changes in Australian Government regulations, all users are
            required to have a valid Australian mobile number associated with
            their account.Your account has been flagged as either not having, or
            having an invalid Australian mobile number. Please update your
            mobile number below:
          </Typography>
        ) : isIOM ? (
          <Typography variant={`body`}>
            Users are required to have a mobile number associated with their
            account. Your account has been flagged as either not having, or
            having an invalid mobile number. Please update your mobile number
            below:
          </Typography>
        ) : null}
        <Form onSubmit={onSubmit}>
          <PhoneInput
            name={`phone`}
            control={control}
            label={`Mobile Number`}
            placeholder={`Enter your mobile number`}
            errorMessage={errors.phone?.message}
            country={(profile?.country as Country) || "AU"}
            withCountryCallingCode
          />

          <Button
            type={`submit`}
            loading={isSubmitting || isPosting}
            className={styles.submitButton}
            gutterTop={false}
          >
            Submit
          </Button>
        </Form>
      </div>
    </FullScreenModal>
  );
};
